import type {BaseComponentProps, BaseComponentState} from "./BaseComponent";
import {BaseComponent, BaseComponentStateInitial} from "./BaseComponent";
import type {NavigateFunction} from "react-router-dom";
import type {CallbackDataLoading, UUID} from "./GeneralInterfaces";
import {
    FrontendURLPaths,
    HTMLFormattedTextTypes,
    IDTypes,
    LabelTextMessageTypes,
    UserLanguages
} from "../Settings";
import {getHTMLFormattedText, getLabelTextMessage} from '../utils/GeneralUtilities';
import * as React from 'react';


/**
 * This interface defines the properties of the class [[`BaseView`]]. All classes deriving from this class must
 * provide properties that extend this interface.
 */
export interface BaseViewProps extends BaseComponentProps {
    /** Allows navigating within the react-router-dom without reloading the website. */
    navigate: NavigateFunction;
    /** Allows passing model id and rollback point id resolved from query string parameters and possibly further
     * API calls for resolution purposes to the MainFrame. */
    provideModelIDs: (transaction_id: UUID | undefined) => void;
    /** Indicates the preferred user language. */
    user_language: UserLanguages;
    /** Stores the current inner height of the browser window for responsive layout calculations. */
    windowInnerHeight: number;
    /** Callback to trigger batch data loading for the above entities. */
    loadData: CallbackDataLoading;
}


/**
 * This interface defines the state of the class [[`BaseView`]]. All classes deriving from this class must
 * use a state interface that derives from this interface.
 */
export interface BaseViewState extends BaseComponentState {
}


/**
 * This constant holds the initialization for all state attributes of the class [[`BaseView`]]. It can be used in the
 * child classes for initializing the state.
 */
export const BaseViewStateInitial: BaseViewState = {
    ...BaseComponentStateInitial,
}


/**
 * This class is the base class for all React components in the frontend that serve as views, i.e. fill the main content
 * area in the main frame. Its main purpose is to provide infrastructure for passing model id and rollback point ids
 * resolved from query-string parameters to the main frame. Furthermore, it provides helper functions for conveniently
 * navigating to other views and checking user access permissions.
 */
export class BaseView<PropsInterface extends BaseViewProps, StateInterface extends BaseViewState>
    extends BaseComponent<PropsInterface, StateInterface> {


    /**
     * This wrapper function can be used to trigger a routing request inside the React-Router-DOM without reloading the
     * entire page. It translates the given parameters into a relative URL that is then passed to the navigate function
     * given in the view component's properties.
     * @param to Target path such as '/time_series/overview'. This basis will then be appended with the provided
     * query-string parameters.
     * @param id_type Type of identifier such as 'model_id' or 'file_id'.
     * @param id UUID of requested resource
     */
    navigate(to: FrontendURLPaths, id_type?: IDTypes, id?: UUID) {
        let url: string = to;
        if (id_type !== undefined && id !== undefined) {
            url += '?' + id_type + '=' + id
        }

        this.props.navigate(url, { replace: false });
    }


    /**
     * Convenience function to getLabelTextMessage from utils. Retrieves a label, text, or message as indicated by the
     * message type in the language requested by the user language props provided in the view. The message can be
     * further enriched by providing additional arguments to this function (rest arguments).This, however, requires
     * that the corresponding message text incorporates template expressions such as ${args[0]} or
     * ${args[1]}, etc...
     * @param message_type Type of message text being requested.
     * @param args Arbitrary number of additional arguments to be incorporated into the message string.
     * @returns The constructed message string in the desired language.
     */
    getLabelTextMessage(message_type: LabelTextMessageTypes, ...args: (string | number)[]): string {
        return getLabelTextMessage(message_type, this.props.user_language, ...args);
    }


    /**
     * Convenience function to getHTMLFormattedText from utils. Retrieves a html-formatted text as indicated by the
     * text type in the language requested by the user language props provided in the view.
     * @param text_type Type of text being requested.
     * @returns The html-formatted text in the desired language.
     */
    getHTMLFormattedText(text_type: HTMLFormattedTextTypes): React.JSX.Element {
        return getHTMLFormattedText(text_type, this.props.user_language);
    }

}
